import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { UserPermissions, PlanFeature } from '../types/permissions';
import { permissionsService } from '../services/permissionsService';
import { useBrandAuth } from './BrandAuthContext/context';


interface PermissionsContextType {
  permissions: UserPermissions | null;
  isLoading: boolean;
  hasFeature: (feature: PlanFeature) => boolean;
  hasAnyFeature: (features: PlanFeature[]) => boolean;
  hasAllFeatures: (features: PlanFeature[]) => boolean;
  refetch: () => Promise<void>;
}

const PermissionsContext = createContext<PermissionsContextType | undefined>(undefined);

interface PermissionsProviderProps {
  children: ReactNode;
}

export function PermissionsProvider({ children }: PermissionsProviderProps) {
  const [permissions, setPermissions] = useState<UserPermissions | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const { user, isLoading: brandAuthLoading } = useBrandAuth();

  const fetchPermissions = async () => {
    // Keep isLoading=true while brand auth is still resolving to prevent a flash of locked UI
    if (brandAuthLoading) {
      return;
    }

    if (!user) {
      setPermissions(null);
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      const data = await permissionsService.getMyPermissions();
      setPermissions(data);
    } catch (error) {
      console.error('Failed to fetch permissions:', error);
      setPermissions(null);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchPermissions();
  }, [user, brandAuthLoading]);

  const hasFeature = (feature: PlanFeature): boolean => {
    return permissionsService.hasFeature(permissions, feature);
  };

  const hasAnyFeature = (features: PlanFeature[]): boolean => {
    return permissionsService.hasAnyFeature(permissions, features);
  };

  const hasAllFeatures = (features: PlanFeature[]): boolean => {
    return permissionsService.hasAllFeatures(permissions, features);
  };

  const value: PermissionsContextType = {
    permissions,
    isLoading,
    hasFeature,
    hasAnyFeature,
    hasAllFeatures,
    refetch: fetchPermissions,
  };

  return (
    <PermissionsContext.Provider value={value}>
      {children}
    </PermissionsContext.Provider>
  );
}

export function usePermissions(): PermissionsContextType {
  const context = useContext(PermissionsContext);
  if (context === undefined) {
    throw new Error('usePermissions must be used within a PermissionsProvider');
  }
  return context;
}
